import type { FunctionComponent } from "react";

import type { ActivityRecord } from "../../../../../stores/jobs.store";
import { Icon, Text, Tooltip } from "@smartsuite/react-ui/lib";
import { Colors } from "@smartsuite/types";
import { t } from "i18next";
import { useRootStore } from "../../../../../stores/hooks";
import { useJobRetry } from "../../../job-retry.hook";
import * as S from "./style";

interface StatusFieldProps {
  value: string;
  record: ActivityRecord;
}

export const StatusField: FunctionComponent<StatusFieldProps> = ({
  value,
  record,
}) => {
  const { retry } = useJobRetry();
  const { limits } = useRootStore();

  const tooltipTextKey = limits.isOverLimit()
    ? limits.canPlanBeUpgraded()
      ? "retry.tooltip-limit-reached-upgrade"
      : "retry.tooltip-limit-reached"
    : "retry.automation-cant-be-rerun";
  const errorMessage =
    typeof record.errorMessage === "string" ? record.errorMessage : "";

  const getTextMessage = (): string => {
    switch (value) {
      case "is_success":
        return "Success";
      case "is_error":
        return "Error";
      case "is_pending":
        return "Running";
      case "partially_done":
        return "Partially done";
      case "conditions_not_met":
        return "Conditions not met";
      case "rejected":
        return "Rejected";
    }
    return "";
  };

  const getColor = (): string => {
    switch (value) {
      case "is_success":
        return Colors.bGreen;
      case "is_error":
        return Colors.bRed;
      case "is_pending":
        return Colors.bOrange;
      case "partially_done":
        return Colors.bYellow;
      case "conditions_not_met":
        return Colors.bPurple;
      case "rejected":
        return Colors.bPink;
    }
    return "";
  };

  return (
    <S.Container color={getColor()}>
      {value === "is_error" && errorMessage !== "" ? (
        <Tooltip
          padding={2}
          text={errorMessage}
          placements={["top"]}
          target={<Text color="reverse">{getTextMessage()}</Text>}
        />
      ) : (
        <Text color="reverse">{getTextMessage()}</Text>
      )}
      {record.isJobCanBeRetried === true && value === "is_error" && (
        <S.RightContainer>
          <S.Pipe />
          <Tooltip
            padding={2}
            text={t("retry.rerun-description")}
            placements={["top"]}
            target={
              <S.RetryContainer
                onClick={() => {
                  void retry(String(record.id));
                }}
              >
                <Icon name="refresh" color={`${Colors.custom1}`} />
              </S.RetryContainer>
            }
          />
        </S.RightContainer>
      )}
      {record.isJobCanBeRetried !== true && value === "is_error" && (
        <S.RightContainer>
          <S.Pipe />
          <Tooltip
            padding={2}
            text={t(tooltipTextKey)}
            placements={["top"]}
            target={
              <S.RetryContainer>
                <Icon name="refresh" color={`${Colors.custom1}80`} />
              </S.RetryContainer>
            }
          />
        </S.RightContainer>
      )}
    </S.Container>
  );
};
